import {
	annualizedDsSalary,
	dsSalaryCoverage,
	dsRegisteredSubtotal,
} from "@/utils/clubSnapshot";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsBar,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsMissingSources,
	DsPreviewBadge,
	DsSection,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubAgents, useClubSnapshot} from "@/utils/hooks";
import {formatEuro} from "@/utils/clubSnapshot";
import {demoAgency} from "@/utils/clubDemo";

function DsEconomyPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});
	const {data: agentsData} = useClubAgents({snapshot: s});
	// Commissioni d'esempio sui contratti reali (null finché i dati non arrivano)
	const agentCommissions =
		s && agentsData
			? (demoAgency(s, agentsData)?.commissions || [])
					.filter((c) => s.playerById.has(c.playerId))
					.reduce((n, c) => n + c.amount, 0)
			: null;

	const teams = s?.teams || [];
	const annualSalary = annualizedDsSalary(s?.monthlySalary);
	const budget = s?.finance.budget;
	const movements = s?.finance.movements || [];
	const marketIn = !movements.length
		? null
		: movements
				.filter((m) => m.direction === "in")
				.reduce((n, m) => n + (parseFloat(m.amount) || 0), 0);
	const marketOut = !movements.length
		? null
		: movements
				.filter((m) => m.direction !== "in")
				.reduce((n, m) => n + (parseFloat(m.amount) || 0), 0);
	const feesExpected = teams.reduce((n, t) => n + t.fees.expected, 0);
	const feesPaid = teams.reduce((n, t) => n + t.fees.paid, 0);
	const maxTeamCost = Math.max(...teams.map((t) => t.monthlySalary), 1);

	// Conto economico: voci reali dove i dati esistono, esempi altrove
	const income = [
		{
			id: "fees",
			label: "Quote di iscrizione",
			value: feesExpected,
			preview: s?.feesPreview,
		},
		{id: "market", label: "Cessioni e bonus di mercato", value: marketIn},
		{id: "sponsor", label: "Sponsorizzazioni", value: 85000, preview: true},
		{
			id: "grants",
			label: "Contributi e premi di valorizzazione",
			value: 22000,
			preview: true,
		},
	];
	const costs = [
		{
			id: "salary",
			label: "Ingaggi giocatori (mensile × 12)",
			value: annualSalary,
		},
		{id: "transfers", label: "Acquisti e prestiti", value: marketOut},
		{
			id: "agents",
			label: "Commissioni procuratori",
			value: agentCommissions,
			preview: true,
		},
		{id: "staff", label: "Compensi staff", value: 96000, preview: true},
		{
			id: "facilities",
			label: "Strutture e utenze",
			value: 41000,
			preview: true,
		},
		{id: "travel", label: "Trasferte e logistica", value: 18000, preview: true},
	];
	const subtotalLabel = (rows) => {
		const total = dsRegisteredSubtotal(rows);
		return `Subtotale dei dati inseriti ${formatEuro(total.value)} · ${total.known}/${total.total} voci con dato · esempi esclusi`;
	};
	const lineColumns = [
		{
			key: "label",
			label: "Voce",
			render: (r) => (
				<>
					{r.label} {r.preview && <DsPreviewBadge />}
				</>
			),
		},
		{
			key: "value",
			label: "Importo",
			align: "right",
			render: (r) => formatEuro(r.value),
		},
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Economia"
					subtitle="Ingaggi da Finanza e voci con dati disponibili; esempi separati dai subtotali"
					snapshot={s}
					scope={scope}
					onReload={reload}
					reloading={loading}
					actions={
						<button
							className="ds-btn ds-btn--ghost"
							onClick={() => navigate("/manager/finance")}
						>
							Gestisci budget
						</button>
					}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi
								label="Budget ingaggi"
								value={formatEuro(budget)}
								sub={
									budget != null
										? "stagionale del contesto DS"
										: "non disponibile nel contesto DS; consulta Finanza"
								}
							/>
							<DsKpi
								label="Monte ingaggi"
								value={formatEuro(annualSalary)}
								sub={`${formatEuro(s.monthlySalary)} al mese · ${dsSalaryCoverage(s)} · mensile × 12, non costo stagione`}
							/>
							<DsKpi
								label="Budget impegnato"
								value={
									budget && annualSalary != null
										? Math.round((annualSalary / budget) * 100) + "%"
										: "—"
								}
								tone={
									!budget || annualSalary == null
										? undefined
										: annualSalary > budget
											? "red"
											: annualSalary / budget > 0.9
												? "yellow"
												: "green"
								}
								sub={
									budget && annualSalary != null
										? `residuo ${formatEuro(budget - annualSalary)}`
										: undefined
								}
							/>
							<DsKpi
								label="Saldo mercato"
								value={formatEuro(
									marketIn === null || marketOut === null
										? null
										: marketIn - marketOut,
								)}
								sub={
									movements.length
										? `${movements.length} movimenti del contesto DS`
										: "Dati non disponibili nel contesto DS; consulta Finanza"
								}
							/>
							<DsKpi
								label="Quote incassate"
								value={formatEuro(feesPaid)}
								sub={`su ${formatEuro(feesExpected)} attese${s.feesPreview ? " · esempio dimostrativo, escluso dai totali" : ""}`}
							/>
						</DsKpiRow>

						<DsSection
							title="Costo rosa per squadra"
							hint="Elenco Finanza alla data odierna. Annualizzato = mensile × 12, non costo della stagione. Contratto condiviso contato una sola volta nel club; i valori per rosa non vanno sommati."
						>
							<DsTable
								columns={[
									{
										key: "name",
										label: "Squadra",
										render: (t) => <b>{t.name}</b>,
									},
									{
										key: "cost",
										label: "Peso",
										render: (t) =>
											t.monthlySalary == null ? (
												"—"
											) : (
												<DsBar value={t.monthlySalary} max={maxTeamCost} />
											),
									},
									{
										key: "monthly",
										label: "Mensile",
										align: "right",
										render: (t) => (
											<span>
												{formatEuro(t.monthlySalary)}
												<small className="ds-muted">
													{" "}
													· {dsSalaryCoverage(t)}
												</small>
											</span>
										),
									},
									{
										key: "annual",
										label: "Annualizzato",
										align: "right",
										render: (t) =>
											formatEuro(annualizedDsSalary(t.monthlySalary)),
									},
									{
										key: "contracts",
										label: "Contratti",
										align: "center",
										render: (t) => t.contracts.length,
									},
								]}
								rows={teams}
							/>
						</DsSection>

						<div className="ds-two-cols">
							<DsSection title="Entrate" hint={subtotalLabel(income)}>
								<DsTable columns={lineColumns} rows={income} />
							</DsSection>
							<DsSection title="Uscite" hint={subtotalLabel(costs)}>
								<DsTable columns={lineColumns} rows={costs} />
							</DsSection>
						</div>
						<div className="ds-muted" style={{fontSize: 12}}>
							Le voci contrassegnate come <DsPreviewBadge /> sono valori di
							esempio: il modulo di contabilità non è ancora attivo.
						</div>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsEconomyPage;
